import { addMonths, differenceInMilliseconds, differenceInMonths, isAfter, isSameDay } from 'date-fns';

import { Text } from '../../../cdl/Text/Text';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { translate } from '../../../common/helpers/translate.helper';
import { MILLISECONDS_IN_DAY } from '../../../common/time.constants';
import { Box } from '../../../common/ui/Box';

interface DurationLeftDisplayProps {
    validUntil?: string;
}

const getDaysRemaining = (validUntil?: string) => {
    if (!validUntil) {
        return null;
    }

    const daysRemaining = differenceInMilliseconds(new Date(validUntil), new Date()) / MILLISECONDS_IN_DAY;
    return Math.max(0, Math.ceil(daysRemaining));
};

const getMonthsRemaining = (validUntil: string) => {
    const now = new Date();
    const endDate = new Date(validUntil);

    if (!isAfter(endDate, now)) {
        return 0;
    }

    const fullMonthsRemaining = differenceInMonths(endDate, now);
    const monthStart = addMonths(now, fullMonthsRemaining);
    const monthEnd = addMonths(now, fullMonthsRemaining + 1);
    const monthFraction =
        differenceInMilliseconds(endDate, monthStart) / differenceInMilliseconds(monthEnd, monthStart);

    return fullMonthsRemaining + monthFraction;
};

const getDurationLeftLabel = (validUntil?: string) => {
    if (!validUntil) {
        return translate('contracts.detail.evergreenValue');
    }

    if (isSameDay(new Date(validUntil), new Date())) {
        return translate('contracts.detail.expiresToday');
    }

    const daysRemaining = getDaysRemaining(validUntil);
    if (!daysRemaining || daysRemaining === 0) {
        return null;
    }

    const monthsRemaining = getMonthsRemaining(validUntil);

    if (monthsRemaining < 1) {
        return daysRemaining === 1
            ? translate('contracts.detail.remainingDay', { days: daysRemaining.toString() })
            : translate('contracts.detail.remainingDays', { days: daysRemaining.toString() });
    }

    const roundedMonthsRemaining = Math.max(1, Math.round(monthsRemaining));

    return roundedMonthsRemaining === 1
        ? translate('contracts.detail.remainingMonth', { months: roundedMonthsRemaining.toString() })
        : translate('contracts.detail.remainingMonths', { months: roundedMonthsRemaining.toString() });
};

export const DurationLeftDisplay = ({ validUntil }: DurationLeftDisplayProps) => {
    const durationLeftLabel = getDurationLeftLabel(validUntil);
    const daysRemaining = getDaysRemaining(validUntil);
    const durationDaysLabel =
        validUntil && daysRemaining && getMonthsRemaining(validUntil) >= 1
            ? translate('contracts.detail.remainingDays', { days: daysRemaining.toString() })
            : null;

    if (!durationLeftLabel) {
        return null;
    }

    return (
        <Box display="grid">
            <Text fontWeight={600}>{translate('contracts.detail.durationLeft')}</Text>
            <Box maxWidth="fit-content">
                {durationDaysLabel ? (
                    <Tooltip label={durationDaysLabel} showArrow side="right">
                        <span>{durationLeftLabel}</span>
                    </Tooltip>
                ) : (
                    <Text>{durationLeftLabel}</Text>
                )}
            </Box>
        </Box>
    );
};
